import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "ghost";
type Size = "md" | "sm";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}

/**
 * The two button styles, as pills: solid crimson for the one primary action on a screen, and an ink outline for
 * everything else (inverts to solid ink on hover). Disabled dims rather than greys so the label stays readable.
 */
const variants: Record<Variant, string> = {
  primary: "bg-accent text-on-accent hover:bg-accent-pressed border-transparent",
  ghost: "bg-transparent text-text border-text hover:bg-text hover:text-white",
};

const sizes: Record<Size, string> = {
  md: "min-h-12 px-[26px] text-[15.5px]",
  sm: "min-h-10 px-[18px] text-[14px]",
};

/** Shared so a `<Link>` styled as a button looks exactly like one. */
export function buttonClass(variant: Variant = "primary", size: Size = "md") {
  return `inline-flex items-center justify-center gap-2 rounded-[var(--radius-button)] border-2 font-bold
    transition-[background-color,color,border-color,transform] duration-[160ms] ease-[var(--ease)] active:scale-[0.97]
    disabled:cursor-not-allowed disabled:opacity-45 ${variants[variant]} ${sizes[size]}`;
}

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  type = "button",
  ...props
}: ButtonProps) {
  return <button type={type} className={`${buttonClass(variant, size)} ${className}`} {...props} />;
}
